import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Modal, Button, ModalFooter } from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { CalloutContext, useStripes, useOkapiKy } from '@folio/stripes/core';
import ScheduleForm from './ScheduleForm';
import useScheduledReportsMutations from '../../hooks/useScheduledReportsMutations';
import { getLocalizedValue } from '../../helpers/localizationUtils';

// ── Helpers ────────────────────────────────────────────────────────────────

const toISO = (v) => {
  if (!v) return null;
  const d = new Date(typeof v === 'number' ? v : String(v));
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const buildStartDate = (formData, timezone) => {
  if (!formData.startDate) return null;
  const tz = timezone || 'UTC';
  // Extract YYYY-MM-DD from ISO string or use as-is if already YYYY-MM-DD
  const date = moment.tz(String(formData.startDate), tz).format('YYYY-MM-DD');

  if (formData.frequency === 'custom') return moment.tz(date, tz).toISOString();

  if (formData.frequency === 'once') {
    const time = (formData.executionTime || '09:00:00').substring(0, 5);
    return moment.tz(`${date}T${time}`, tz).toISOString();
  }

  if (formData.frequency === 'hourly') {
    const mm = String(formData.executionMinute ?? '0').padStart(2, '0');
    return moment.tz(`${date}T00:${mm}`, tz).toISOString();
  }

  const time = (formData.executionTime || '09:00:00').substring(0, 5);

  if (formData.frequency === 'weekly') {
    const DOW_MAP = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
    const targetDay = DOW_MAP[formData.dayOfWeek ?? 'MON'];
    const m = moment.tz(`${date}T${time}`, tz);
    const diff = (targetDay - m.day() + 7) % 7 || 7;
    return m.add(diff, 'days').toISOString();
  }

  if (formData.frequency === 'monthly') {
    const dom = parseInt(formData.dayOfMonth ?? '1', 10);
    const m = moment.tz(`${date}T${time}`, tz).date(dom);
    if (m.isBefore(moment())) m.add(1, 'month');
    return m.toISOString();
  }

  if (formData.frequency === 'yearly') {
    const dom = parseInt(formData.dayOfMonth ?? '1', 10);
    const moy = parseInt(formData.monthOfYear ?? '1', 10);
    const m = moment.tz(`${date}T${time}`, tz).month(moy - 1).date(dom);
    if (m.isBefore(moment())) m.add(1, 'year');
    return m.toISOString();
  }

  return moment.tz(`${date}T${time}`, tz).toISOString();
};

const buildEndDate = (formData, timezone) => {
  if (!formData.endDate) return null;
  const tz   = timezone || 'UTC';
  const date = moment.tz(String(formData.endDate), tz).format('YYYY-MM-DD');
  const time = (formData.endTime || '23:59:00').substring(0, 5);
  return moment.tz(`${date}T${time}`, tz).toISOString();
};

// Pass ISO string directly to Datepicker — it handles parsing internally
// same as ui-users UserEdit which passes expirationDate ISO string directly
const toDatepickerValue = (isoStr) => {
  if (!isoStr) return '';
  return isoStr;
};

// Quartz cron format: second minute hour dayOfMonth month dayOfWeek
// extractTime reads hour/minute from cron expression (always UTC)
const extractTime = (schedule, timezone) => {
  const tz = timezone || 'UTC';
  if (schedule.cronExpression) {
    const parts = schedule.cronExpression.split(' ');
    if (parts.length >= 3 && parts[2] !== '*' && parts[1] !== '*')
      return `${String(parts[2]).padStart(2, '0')}:${String(parts[1]).padStart(2, '0')}:00`;
  }
  if (schedule.startDate) {
    const m = moment.tz(schedule.startDate, tz);
    if (m.isValid())
      return `${String(m.hours()).padStart(2, '0')}:${String(m.minutes()).padStart(2, '0')}:00`;
  }
  return '09:00:00';
};

const extractMinute = (schedule) => {
  if (schedule.cronExpression) {
    const parts = schedule.cronExpression.split(' ');
    if (parts.length >= 2 && parts[1] !== '*') return parts[1];
  }
  return schedule.startDate
    ? String(new Date(schedule.startDate).getUTCMinutes())
    : '0';
};

const extractDayOfWeek = (schedule) => {
  if (schedule.cronExpression) {
    const parts = schedule.cronExpression.split(' ');
    if (parts.length >= 6 && parts[5] !== '?' && parts[5] !== '*') return parts[5];
  }
  if (schedule.startDate) {
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    return days[new Date(schedule.startDate).getUTCDay()] || 'MON';
  }
  return 'MON';
};

const extractDayOfMonth = (schedule) => {
  if (schedule.cronExpression) {
    const parts = schedule.cronExpression.split(' ');
    if (parts.length >= 4 && parts[3] !== '*' && parts[3] !== '?') return parts[3];
  }
  return schedule.startDate
    ? String(new Date(schedule.startDate).getUTCDate())
    : '1';
};

// Extract end time from endDate ISO string using tenant timezone
const extractEndTime = (schedule, timezone) => {
  if (!schedule.endDate) return '23:59:00';
  const tz = timezone || 'UTC';
  const m = moment.tz(schedule.endDate, tz);
  if (!m.isValid()) return '23:59:00';
  return `${String(m.hours()).padStart(2, '0')}:${String(m.minutes()).padStart(2, '0')}:00`;
};

const extractMonthOfYear = (schedule) => {
  if (schedule.cronExpression) {
    const parts = schedule.cronExpression.split(' ');
    if (parts.length >= 5 && parts[4] !== '*' && parts[4] !== '?') return parts[4];
  }
  return schedule.startDate
    ? String(new Date(schedule.startDate).getUTCMonth() + 1)
    : '1';
};

// Convert a field that may be a legacy string or array into a localized array
const toLocalizedArray = (field, defaultLocale = 'en') => {
  if (!field) return [];
  if (Array.isArray(field)) return field;
  if (typeof field === 'string' && field.trim())
    return [{ locale: defaultLocale, value: field }];
  return [];
};

// ── Component ──────────────────────────────────────────────────────────────

function ScheduleEditModal({ open, onClose, schedule, onSuccess }) {
  const { updateSchedule } = useScheduledReportsMutations();
  const callout        = useContext(CalloutContext);
  const intl           = useIntl();
  const { timezone }   = useStripes();
  const { locale }     = intl;
  const okapiKy        = useOkapiKy();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef        = React.useRef(null);
  const [isPristine,   setIsPristine]   = React.useState(true);

  // Track pristine state from form
  const handleFormRef = React.useCallback((form) => {
    formRef.current = form;
    if (form) {
      form.subscribe(
        ({ pristine }) => setIsPristine(pristine),
        { pristine: true }
      );
    }
  }, []);

  const initialValues = useMemo(() => ({
    name:            toLocalizedArray(schedule.name),
    description:     toLocalizedArray(schedule.description),
    frequency:       schedule.frequency      || 'daily',
    cronExpression:  schedule.cronExpression || '',
    startDate:       toDatepickerValue(schedule.startDate),
    endDate:         toDatepickerValue(schedule.endDate),
    endTime:         extractEndTime(schedule, timezone),
    outputFormat:    schedule.outputFormat   || 'excel',
    recipients:      (schedule.recipients || []).map(r => ({
      email:  r.email  || '',
      name:   r.name   || '',
      locale: r.locale || 'en',
    })),
    emailSubject:    toLocalizedArray(schedule.emailSubject),
    emailBody:       toLocalizedArray(schedule.emailBody),
    executionTime:   extractTime(schedule, timezone),
    executionMinute: extractMinute(schedule),
    dayOfWeek:       extractDayOfWeek(schedule),
    dayOfMonth:      extractDayOfMonth(schedule),
    monthOfYear:     extractMonthOfYear(schedule),
  }), [schedule.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const payload = {
        id:             schedule.id,
        reportId:       schedule.reportId,
        userId:         schedule.userId,
        name:           formData.name || [],
        frequency:      formData.frequency,
        outputFormat:   formData.outputFormat,
        recipients:     formData.recipients || [],
        isActive:       schedule.isActive,
        executionCount: schedule.executionCount ?? 0,
        startDate:      buildStartDate(formData, timezone),
      };

      if (formData.description?.length)  payload.description  = formData.description;
      if (formData.emailSubject?.length) payload.emailSubject = formData.emailSubject;
      if (formData.emailBody?.length)    payload.emailBody    = formData.emailBody;
      if (formData.frequency === 'custom' && formData.cronExpression)
        payload.cronExpression = formData.cronExpression;
      // nextExecutionDate is recalculated by the backend on every PUT — do not send it to avoid overwriting the new value
      if (schedule.lastExecutionDate) payload.lastExecutionDate = schedule.lastExecutionDate;

      const endDate = buildEndDate(formData, timezone);
      if (endDate) payload.endDate = endDate;

      await updateSchedule.mutateAsync({ scheduleId: schedule.id, updates: payload });

      // Fetch updated schedule from API to get recalculated nextExecutionDate
      let updatedSchedule = payload;
      try {
        updatedSchedule = await okapiKy.get(`scheduled-reports/${schedule.id}`).json();
      } catch (_) { /* fallback to payload */ }

      callout.sendCallout({
        message: intl.formatMessage({ id: 'ui-reports.scheduledReports.callout.updated' }),
        type: 'success',
      });

      if (onSuccess) onSuccess(updatedSchedule);
      // onSuccess is responsible for closing the modal
    } catch (error) {
      callout.sendCallout({
        message: intl.formatMessage(
          { id: 'ui-reports.scheduledReports.callout.updateError' },
          { error: error.message },
        ),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      label={
        <FormattedMessage
          id="ui-reports.scheduledReports.editModal.title"
          defaultMessage="Edit Schedule: {name}"
          values={{ name: getLocalizedValue(schedule.name, locale) || '' }}
        />
      }
      footer={
        <ModalFooter>
          <Button
            buttonStyle="primary"
            disabled={isSubmitting || isPristine}
            marginBottom0
            onClick={() => formRef.current && formRef.current.submit()}
          >
            <FormattedMessage id="ui-reports.scheduledReports.saveEdit" />
          </Button>
          <Button onClick={onClose} disabled={isSubmitting} marginBottom0>
            <FormattedMessage id="ui-reports.button.cancel" />
          </Button>
        </ModalFooter>
      }
      onClose={onClose}
      size="large"
    >
      <ScheduleForm
        onSubmit={handleSubmit}
        initialValues={initialValues}
        isEdit
        formRef={handleFormRef}
      />
    </Modal>
  );
}

ScheduleEditModal.propTypes = {
  open:      PropTypes.bool.isRequired,
  onClose:   PropTypes.func.isRequired,
  schedule:  PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
};

export default ScheduleEditModal;
